import React, { useEffect, useState } from 'react';
import {
  Form,
  Input,
  Button,
  message,
  Row,
  Col,
  Breadcrumb,
  Checkbox,
  Spin,
  Grid,
  Card,
  Typography,
  Space,
  Tag,
  Tooltip,
  Alert,
  ColorPicker,
  Select,
} from 'antd';
import {
  RightOutlined,
  SaveOutlined,
  CrownOutlined,
  CustomerServiceOutlined,
  DollarOutlined,
  ShopOutlined,
  SettingOutlined,
  LockOutlined,
  AppstoreOutlined,
  CheckCircleOutlined,
} from '@ant-design/icons';
import { useNavigate, Link, useParams } from 'react-router-dom';
import { rolesService } from '../../common/services';

const { Title, Text } = Typography;

// Icônes disponibles pour les rôles
const AVAILABLE_ICONS = [
  { value: 'crown', label: 'Couronne', icon: <CrownOutlined /> },
  { value: 'customer-service', label: 'Support', icon: <CustomerServiceOutlined /> },
  { value: 'dollar', label: 'Dollar', icon: <DollarOutlined /> },
  { value: 'shop', label: 'Boutique', icon: <ShopOutlined /> },
  { value: 'setting', label: 'Paramètre', icon: <SettingOutlined /> },
  { value: 'lock', label: 'Verrou', icon: <LockOutlined /> },
];

const ICON_MAP = {
  crown: <CrownOutlined />,
  'customer-service': <CustomerServiceOutlined />,
  dollar: <DollarOutlined />,
  shop: <ShopOutlined />,
  setting: <SettingOutlined />,
  lock: <LockOutlined />,
};

const AjoutRole = () => {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = !!id;

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [selectedPerms, setSelectedPerms] = useState([]);
  const [permList, setPermList] = useState([]);
  const [groupOrder, setGroupOrder] = useState([]);
  const [groupLabels, setGroupLabels] = useState({});
  const [existingRole, setExistingRole] = useState(null);
  const [selectedColor, setSelectedColor] = useState('#595959');
  const [selectedIcon, setSelectedIcon] = useState('setting');

  const screens = Grid.useBreakpoint();
  const isMobile = !screens.sm;

  // Charger les permissions et le rôle (si modification)
  useEffect(() => {
    const loadData = async () => {
      try {
        // Charger les permissions
        const permResponse = await rolesService.getPermissions();
        if (permResponse?.success) {
          const permsData = permResponse.data;
          const list = permsData?.list || [];
          setPermList(list);
          setGroupLabels(permsData?.groupLabels || {});
          const order = [...new Set(list.map((i) => i.group).filter(Boolean))];
          setGroupOrder(order);
        }

        // Si mode édition, charger le rôle
        if (isEditMode) {
          const roleResponse = await rolesService.getById(id);
          if (roleResponse?.success) {
            const role = roleResponse.data;
            setExistingRole(role);
            setSelectedPerms(role.permissions || []);
            setSelectedColor(role.color || '#595959');
            setSelectedIcon(role.icon || 'setting');
            form.setFieldsValue({
              name: role.name,
              label: role.label,
              description: role.description,
              permissions: role.permissions || [],
              color: role.color,
              icon: role.icon,
            });
          }
        }
      } catch (e) {
        console.error(e);
        message.error('Erreur lors du chargement des données');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [id, isEditMode, form]);

  const handleSubmit = async (values) => {
    const payload = {
      ...values,
      permissions: selectedPerms,
      color: selectedColor,
      icon: selectedIcon,
    };

    try {
      setSubmitting(true);

      if (isEditMode) {
        await rolesService.update(id, payload);
        message.success('Rôle modifié avec succès');
      } else {
        await rolesService.create(payload);
        message.success('Rôle créé avec succès');
      }

      navigate('/dashboard/roles');
    } catch (error) {
      console.error(error);
      message.error(error?.message || `Erreur lors de ${isEditMode ? 'la modification' : "l'ajout"} du rôle`);
    } finally {
      setSubmitting(false);
    }
  };

  // Compter les permissions sélectionnées par groupe
  const getGroupPermCount = (groupKey) => {
    const groupPerms = permList.filter((p) => p.group === groupKey);
    const selectedInGroup = groupPerms.filter((p) => selectedPerms.includes(p.key));
    return { selected: selectedInGroup.length, total: groupPerms.length };
  };

  // Sélectionner/désélectionner toutes les permissions d'un groupe
  const toggleGroupPermissions = (groupKey) => {
    const groupPerms = permList.filter((p) => p.group === groupKey).map((p) => p.key);
    const allSelected = groupPerms.every((p) => selectedPerms.includes(p));

    let newPerms;
    if (allSelected) {
      newPerms = selectedPerms.filter((p) => !groupPerms.includes(p));
    } else {
      newPerms = [...new Set([...selectedPerms, ...groupPerms])];
    }

    setSelectedPerms(newPerms);
    form.setFieldsValue({ permissions: newPerms });
  };

  // Sélectionner toutes les permissions
  const selectAllPermissions = () => {
    const allPerms = permList.map((p) => p.key);
    setSelectedPerms(allPerms);
    form.setFieldsValue({ permissions: allPerms });
  };

  // Désélectionner toutes les permissions
  const deselectAllPermissions = () => {
    setSelectedPerms([]);
    form.setFieldsValue({ permissions: [] });
  };

  return (
    <>
      {/* Breadcrumb */}
      <Breadcrumb
        separator={<RightOutlined />}
        items={[
          { title: <Link to="/dashboard">Accueil</Link> },
          { title: <Link to="/dashboard/roles">Rôles</Link> },
          { title: isEditMode ? 'Modifier le rôle' : 'Nouveau rôle' },
        ]}
        style={{ marginBottom: 16 }}
      />

      {loading ? (
        <Card>
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '80px 0' }}>
            <Spin size="large" tip="Chargement des données..." />
          </div>
        </Card>
      ) : (
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          initialValues={{ icon: 'setting', permissions: [] }}
        >
          <Row gutter={[24, 24]}>
            {/* Colonne gauche - Informations du rôle */}
            <Col xs={24} lg={10}>
              <Card
                title={
                  <Space>
                    <SettingOutlined style={{ color: '#1890ff' }} />
                    <span>Informations du rôle</span>
                  </Space>
                }
                style={{ height: '100%' }}
                headStyle={{ borderBottom: '2px solid #1890ff' }}
              >
                {existingRole?.isSystem && (
                  <Alert
                    message="Rôle système"
                    description="Ce rôle est un rôle système. Son nom ne peut pas être modifié."
                    type="warning"
                    showIcon
                    style={{ marginBottom: 16 }}
                  />
                )}

                <Form.Item
                  name="label"
                  label={<Text strong>Libellé</Text>}
                  rules={[{ required: true, message: 'Le libellé est requis' }]}
                >
                  <Input placeholder="Ex : Responsable Marketing" size="large" />
                </Form.Item>

                <Form.Item
                  name="name"
                  label={<Text strong>Nom technique</Text>}
                  rules={[
                    { required: true, message: 'Le nom technique est requis' },
                    { pattern: /^[a-z0-9_-]+$/, message: 'Lettres minuscules, chiffres, - et _ uniquement' },
                  ]}
                  normalize={(v) => v?.toLowerCase().replace(/\s+/g, '-')}
                >
                  <Input
                    placeholder="Ex : responsable-marketing"
                    size="large"
                    disabled={existingRole?.isSystem}
                  />
                </Form.Item>

                <Form.Item
                  name="description"
                  label={<Text strong>Description</Text>}
                >
                  <Input.TextArea
                    placeholder="Description des responsabilités de ce rôle..."
                    rows={3}
                  />
                </Form.Item>

                {/* Couleur */}
                <Form.Item label={<Text strong>Couleur</Text>}>
                  <Space>
                    <ColorPicker
                      value={selectedColor}
                      onChange={(_, hex) => setSelectedColor(hex)}
                      showText
                    />
                    <Tag color={selectedColor} icon={ICON_MAP[selectedIcon]}>
                      Aperçu
                    </Tag>
                  </Space>
                </Form.Item>

                {/* Icône */}
                <Form.Item label={<Text strong>Icône</Text>}>
                  <Select
                    value={selectedIcon}
                    onChange={setSelectedIcon}
                    size="large"
                    style={{ width: '100%' }}
                  >
                    {AVAILABLE_ICONS.map((icon) => (
                      <Select.Option key={icon.value} value={icon.value}>
                        <Space>
                          {icon.icon}
                          <span>{icon.label}</span>
                        </Space>
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>

                {/* Aperçu du rôle */}
                <Card
                  size="small"
                  style={{
                    backgroundColor: `${selectedColor}10`,
                    border: `1px solid ${selectedColor}30`,
                  }}
                >
                  <Space direction="vertical" size={4} style={{ width: '100%' }}>
                    <Space>
                      <Tag color={selectedColor} icon={ICON_MAP[selectedIcon]} style={{ padding: '4px 12px' }}>
                        {form.getFieldValue('label') || 'Nom du rôle'}
                      </Tag>
                    </Space>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {form.getFieldValue('description') || 'Description du rôle'}
                    </Text>
                    <div style={{ marginTop: 8 }}>
                      <Tag color="blue">
                        {selectedPerms.length} permission{selectedPerms.length > 1 ? 's' : ''} sélectionnée{selectedPerms.length > 1 ? 's' : ''}
                      </Tag>
                    </div>
                  </Space>
                </Card>
              </Card>
            </Col>

            {/* Colonne droite - Permissions */}
            <Col xs={24} lg={14}>
              <Card
                title={
                  <Row justify="space-between" align="middle">
                    <Space>
                      <LockOutlined style={{ color: '#52c41a' }} />
                      <span>Permissions</span>
                    </Space>
                    <Space>
                      <Button size="small" onClick={selectAllPermissions}>
                        Tout sélectionner
                      </Button>
                      <Button size="small" onClick={deselectAllPermissions}>
                        Tout désélectionner
                      </Button>
                      <Tag color="blue">
                        {selectedPerms.length} / {permList.length}
                      </Tag>
                    </Space>
                  </Row>
                }
                headStyle={{ borderBottom: '2px solid #52c41a' }}
              >
                <Alert
                  message="Définissez les accès de ce rôle"
                  description="Cliquez sur le titre d'un groupe pour sélectionner/désélectionner toutes ses permissions."
                  type="info"
                  showIcon
                  style={{ marginBottom: 16 }}
                />

                <Form.Item name="permissions">
                  <Checkbox.Group
                    style={{ width: '100%' }}
                    value={selectedPerms}
                    onChange={(vals) => {
                      setSelectedPerms(vals);
                      form.setFieldsValue({ permissions: vals });
                    }}
                  >
                    <Row gutter={[16, 16]}>
                      {groupOrder.map((g) => {
                        const items = permList.filter((p) => p.group === g);
                        if (!items.length) return null;
                        const { selected, total } = getGroupPermCount(g);
                        const allSelected = selected === total;
                        const someSelected = selected > 0 && selected < total;

                        return (
                          <Col xs={24} md={12} key={g}>
                            <Card
                              size="small"
                              style={{
                                borderColor: allSelected ? '#52c41a' : someSelected ? '#faad14' : '#d9d9d9',
                                backgroundColor: allSelected ? '#f6ffed' : someSelected ? '#fffbe6' : '#fff',
                              }}
                              bodyStyle={{ padding: 12 }}
                            >
                              {/* Header du groupe */}
                              <div
                                onClick={() => toggleGroupPermissions(g)}
                                style={{
                                  cursor: 'pointer',
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  alignItems: 'center',
                                  marginBottom: 8,
                                  paddingBottom: 8,
                                  borderBottom: '1px solid #f0f0f0',
                                }}
                              >
                                <Space>
                                  <span style={{ color: '#1890ff' }}>
                                    <AppstoreOutlined />
                                  </span>
                                  <Text strong style={{ fontSize: 13 }}>
                                    {groupLabels?.[g] || g}
                                  </Text>
                                </Space>
                                <Tooltip title={`${selected}/${total} permissions`}>
                                  <Tag
                                    color={allSelected ? 'success' : someSelected ? 'warning' : 'default'}
                                    style={{ marginRight: 0 }}
                                  >
                                    {allSelected && <CheckCircleOutlined style={{ marginRight: 4 }} />}
                                    {selected}/{total}
                                  </Tag>
                                </Tooltip>
                              </div>

                              {/* Permissions du groupe */}
                              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                                {items.map((p) => (
                                  <Checkbox
                                    key={p.key}
                                    value={p.key}
                                    style={{
                                      marginLeft: 0,
                                      padding: '4px 8px',
                                      borderRadius: 4,
                                      backgroundColor: selectedPerms.includes(p.key) ? '#e6f7ff' : 'transparent',
                                    }}
                                  >
                                    <Text style={{ fontSize: 12 }}>{p.label}</Text>
                                  </Checkbox>
                                ))}
                              </div>
                            </Card>
                          </Col>
                        );
                      })}

                      {/* Permissions sans groupe */}
                      {permList.some((p) => !p.group) && (
                        <Col xs={24}>
                          <Card size="small" style={{ backgroundColor: '#fafafa' }}>
                            <div style={{ fontWeight: 600, marginBottom: 8 }}>
                              <Space>
                                <AppstoreOutlined />
                                Autres permissions
                              </Space>
                            </div>
                            <Row gutter={[8, 8]}>
                              {permList
                                .filter((p) => !p.group)
                                .map((p) => (
                                  <Col key={p.key} xs={24} sm={12} md={8}>
                                    <Checkbox value={p.key}>{p.label}</Checkbox>
                                  </Col>
                                ))}
                            </Row>
                          </Card>
                        </Col>
                      )}
                    </Row>
                  </Checkbox.Group>
                </Form.Item>
              </Card>
            </Col>
          </Row>

          {/* Boutons d'action */}
          <Card style={{ marginTop: 24 }}>
            <Row justify="space-between" align="middle">
              <Col>
                <Text type="secondary">
                  Tous les champs marqués d'un astérisque (*) sont obligatoires
                </Text>
              </Col>
              <Col>
                <Space>
                  <Button size="large" onClick={() => navigate('/dashboard/roles')}>
                    Annuler
                  </Button>
                  <Button
                    type="primary"
                    size="large"
                    htmlType="submit"
                    loading={submitting}
                    icon={<SaveOutlined />}
                    style={{
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      border: 'none',
                    }}
                  >
                    {submitting ? 'Enregistrement...' : isEditMode ? 'Modifier le rôle' : 'Créer le rôle'}
                  </Button>
                </Space>
              </Col>
            </Row>
          </Card>
        </Form>
      )}
    </>
  );
};

export default AjoutRole;
